import type { SerializedEditorStateJson } from "./types.js";

/**
 * Walk a serialized Lexical state and concatenate all `text` fields it
 * contains. Used to derive a draft title and preview without invoking the
 * editor at all (so this works in worker / non-React contexts).
 */
export function extractPlainText(state: SerializedEditorStateJson): string {
  const out: string[] = [];
  walk(state, out);
  return out.join("").replace(/\s+/g, " ").trim();
}

interface NodeLike {
  type?: string;
  text?: string;
  children?: NodeLike[];
}

function walk(value: unknown, out: string[]): void {
  if (!value || typeof value !== "object") return;
  if (Array.isArray(value)) {
    for (const v of value) walk(v, out);
    return;
  }
  const node = value as NodeLike;
  if (typeof node.text === "string") {
    out.push(node.text);
    out.push(" ");
  }
  if (Array.isArray(node.children)) {
    for (const c of node.children) {
      walk(c, out);
      if (c.type === "paragraph" || c.type === "linebreak") {
        out.push(" ");
      }
    }
  }
  for (const v of Object.values(value)) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      // Recurse into objects we haven't already handled (e.g. `root`)
      walk(v, out);
    }
  }
}

export function deriveTitleAndPreview(state: SerializedEditorStateJson): {
  title: string;
  preview: string;
} {
  const text = extractPlainText(state);
  const firstLine = text.split(/[.!?\n]/)[0]?.trim() ?? "";
  const title = (firstLine || "Untitled").slice(0, 60);
  const preview = text.slice(0, 120);
  return { title, preview };
}
